import { useState, useEffect, useMemo } from 'react';
import StorageService from '../services/storageService';
import { KEYS } from '../services/initService';
import { useBusinessSettings } from '../context/BusinessSettingsContext';
import { computeCartTax } from '../data/businessDefaults';

function generateOrderId() {
  const now = new Date();
  const ymd = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`;
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `TE-${ymd}-${rand}`;
}

// Simulated pickup slots (production: generate from backend based on capacity)
function getPickupSlots() {
  const slots = [];
  const now = new Date();
  // round up to next 15-min block + 20 min prep
  const base = new Date(now.getTime() + 20 * 60 * 1000);
  const mins = Math.ceil(base.getMinutes() / 15) * 15;
  base.setMinutes(mins, 0, 0);
  for (let i = 0; i < 8; i++) {
    const t = new Date(base.getTime() + i * 15 * 60 * 1000);
    const hh = String(t.getHours()).padStart(2, '0');
    const mm = String(t.getMinutes()).padStart(2, '0');
    slots.push({ value: t.toISOString(), label: `${hh}:${mm}` });
  }
  return slots;
}

export default function CartPanel({ cart, isOpen, onClose, onUpdateQty, onRemove, onClearOrder }) {
  const { business, formatPrice } = useBusinessSettings();
  const [step, setStep] = useState('cart'); // 'cart' | 'checkout' | 'success'
  const [form, setForm] = useState({ name: '', phone: '', email: '', pickupSlot: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const slots = getPickupSlots();

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const tax = useMemo(() => computeCartTax(subtotal, business), [subtotal, business]);

  useEffect(() => {
    if (!isOpen) setDeleteConfirmOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!deleteConfirmOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setDeleteConfirmOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteConfirmOpen]);

  const confirmDeleteOrder = () => {
    if (cart.length === 0) {
      setDeleteConfirmOpen(false);
      return;
    }
    onClearOrder();
    setDeleteConfirmOpen(false);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your name.';
    if (!form.phone.trim()) e.phone = 'Please enter your phone number.';
    if (!form.pickupSlot) e.pickupSlot = 'Please choose a pickup time.';
    return e;
  };

  const handleConfirm = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const orderId = generateOrderId();
    const tx = computeCartTax(subtotal, business);
    const order = {
      id: orderId,
      orderId,
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      pickupSlot: form.pickupSlot,
      pickupLabel: slots.find((s) => s.value === form.pickupSlot)?.label || form.pickupSlot,
      notes: form.notes,
      items: cart.map((i) => ({ itemId: i.id, name: i.name, emoji: i.emoji, price: i.price, qty: i.qty })),
      subtotal: tx.subtotal,
      vatAmount: tx.vatAmount,
      vatRatePercent: tx.vatRatePercent,
      pricesIncludeVat: tx.pricesIncludeVat,
      total: tx.totalDue,
      paymentStatus: 'paid', // mock: in production, set after payment gateway callback
      status: 'new',
      createdAt: new Date().toISOString(),
    };
    await StorageService.push(KEYS.ORDERS, order);
    setConfirmedOrder(order);
    setStep('success');
  };

  const resetAndClose = () => {
    setStep('cart');
    setForm({ name: '', phone: '', email: '', pickupSlot: '', notes: '' });
    setErrors({});
    setConfirmedOrder(null);
    onClose(true); // true = clear cart
  };

  const panelStyle = {
    position: 'fixed',
    top: 0, right: 0, bottom: 0,
    width: 'min(440px, 100vw)',
    background: 'white',
    zIndex: 1100,
    boxShadow: 'var(--shadow-panel)',
    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform .32s cubic-bezier(.4,0,.2,1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={() => onClose(false)}
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.42)',
          zIndex: 1050,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'all' : 'none',
          transition: 'opacity .3s',
        }}
      />

      {/* Panel */}
      <div style={panelStyle} role="dialog" aria-label="Your order" aria-modal="true">
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--dark)' }}>
            {step === 'cart' ? 'Your order' : step === 'checkout' ? 'Confirm Details' : 'Order Confirmed'}
          </h2>
          <button
            onClick={step === 'success' ? resetAndClose : () => onClose(false)}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'var(--teal-pale)', border: 'none',
              color: 'var(--teal)', fontSize: '1rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all .18s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--teal)'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--teal-pale)'; e.currentTarget.style.color = 'var(--teal)'; }}
            aria-label="Close cart"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* STEP: CART */}
          {step === 'cart' && (
            <div style={{ padding: '1rem 1.5rem' }}>
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2.5rem 1.25rem 2rem', color: 'var(--gray)' }}>
                  <p style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--dark)', marginBottom: '.85rem' }}>
                    Your basket is empty
                  </p>
                  <p style={{ fontSize: '.92rem', lineHeight: 1.65, marginBottom: '1.25rem', maxWidth: 320, margin: '0 auto 1.25rem' }}>
                    Pick dishes from the menu, tap <strong style={{ color: 'var(--teal-dark)' }}>Add</strong> on each card, then return here to review and pay when you are ready.
                  </p>
                  <button
                    type="button"
                    className="btn btn-teal"
                    style={{ width: '100%', maxWidth: 280, justifyContent: 'center', padding: '.85rem 1rem' }}
                    onClick={() => {
                      onClose(false);
                      window.setTimeout(() => {
                        document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
                      }, 200);
                    }}
                  >
                    Browse the menu
                  </button>
                </div>
              ) : (
                cart.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '1rem',
                      padding: '.85rem 0',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <div style={{ fontSize: '2rem', minWidth: 44, textAlign: 'center' }}>{item.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '.9rem', color: 'var(--dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.name}
                      </div>
                      <div style={{ color: 'var(--teal)', fontWeight: 700, fontSize: '.88rem' }}>
                        {formatPrice(item.price)} each
                      </div>
                    </div>
                    {/* Qty controls */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem' }}>
                      <button
                        onClick={() => onUpdateQty(item.id, item.qty - 1)}
                        style={{
                          width: 28, height: 28, borderRadius: '50%',
                          border: '1.5px solid var(--border)', background: 'none',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1rem', color: 'var(--dark)', transition: 'all .15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.color = 'var(--teal)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--dark)'; }}
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span style={{ fontWeight: 600, fontSize: '.9rem', minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                      <button
                        onClick={() => onUpdateQty(item.id, item.qty + 1)}
                        style={{
                          width: 28, height: 28, borderRadius: '50%',
                          border: '1.5px solid var(--border)', background: 'none',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1rem', color: 'var(--dark)', transition: 'all .15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.color = 'var(--teal)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--dark)'; }}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--dark)', minWidth: 60, textAlign: 'right' }}>
                      {formatPrice(item.price * item.qty)}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* STEP: CHECKOUT */}
          {step === 'checkout' && (
            <div style={{ padding: '1.25rem 1.5rem' }}>
              {/* Pickup time */}
              <div className="form-group">
                <label className="form-label" htmlFor="co-pickup">Pickup Time *</label>
                <select
                  id="co-pickup"
                  className="form-select"
                  value={form.pickupSlot}
                  onChange={(e) => setForm({ ...form, pickupSlot: e.target.value })}
                >
                  <option value="">Choose your pickup time</option>
                  {slots.map((s) => (
                    <option key={s.value} value={s.value}>Today at {s.label}</option>
                  ))}
                </select>
                {errors.pickupSlot && <p style={{ color: 'var(--red)', fontSize: '.78rem', marginTop: '.3rem' }}>{errors.pickupSlot}</p>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="co-name">Full Name *</label>
                <input id="co-name" className="form-input" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                {errors.name && <p style={{ color: 'var(--red)', fontSize: '.78rem', marginTop: '.3rem' }}>{errors.name}</p>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="co-phone">Phone Number *</label>
                <input id="co-phone" className="form-input" type="tel" placeholder="+248 xxx xxxx" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                {errors.phone && <p style={{ color: 'var(--red)', fontSize: '.78rem', marginTop: '.3rem' }}>{errors.phone}</p>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="co-email">Email (optional, for receipt)</label>
                <input id="co-email" className="form-input" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="co-notes">Special Requests / Spice Level</label>
                <textarea id="co-notes" className="form-textarea" placeholder="e.g. no peanuts, extra spicy, mild sauce, allergen info..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>

              {/* Payment notice */}
              <div style={{
                background: 'var(--teal-pale)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: '1rem',
                fontSize: '.85rem',
                color: 'var(--teal-dark)',
                lineHeight: 1.6,
                marginTop: '.5rem',
              }}
              >
                🔒 <strong>Secure payment</strong> is processed after this step. Your order is only confirmed once payment is complete. You will receive a confirmation code to show at the kiosk.
                {business.paymentNote && (
                  <span style={{ display: 'block', marginTop: '.65rem' }}>{business.paymentNote}</span>
                )}
              </div>
            </div>
          )}

          {/* STEP: SUCCESS */}
          {step === 'success' && confirmedOrder && (
            <div style={{ padding: '2.5rem 1.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: '3.5rem', marginBottom: '1rem', animation: 'pop .5s cubic-bezier(.34,1.56,.64,1)' }}>✅</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--dark)', marginBottom: '.6rem' }}>
                Order Confirmed!
              </h3>
              <p style={{ color: 'var(--gray)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                Your order has been received and payment processed. Show your order ID at the kiosk.
              </p>

              {/* Order card */}
              <div style={{
                background: 'var(--teal-pale)',
                borderRadius: 'var(--radius)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                textAlign: 'left',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.75rem' }}>
                  <span style={{ fontSize: '.78rem', color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Order ID</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--teal-dark)', fontSize: '.95rem' }}>{confirmedOrder.orderId}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.75rem' }}>
                  <span style={{ fontSize: '.78rem', color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Pickup Time</span>
                  <span style={{ fontWeight: 700, color: 'var(--dark)' }}>Today at {confirmedOrder.pickupLabel}</span>
                </div>
                {confirmedOrder.pricesIncludeVat === false && Number(confirmedOrder.vatAmount) > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.45rem', fontSize: '.82rem', color: 'var(--gray)' }}>
                      <span>Subtotal (excl. VAT)</span>
                      <span>{formatPrice(confirmedOrder.subtotal)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.45rem', fontSize: '.82rem', color: 'var(--gray)' }}>
                      <span>VAT ({confirmedOrder.vatRatePercent ?? business.vatRatePercent}%)</span>
                      <span>{formatPrice(confirmedOrder.vatAmount)}</span>
                    </div>
                  </>
                )}
                {confirmedOrder.pricesIncludeVat !== false && Number(confirmedOrder.vatAmount) > 0 && (
                  <p style={{ fontSize: '.72rem', color: 'var(--gray)', marginBottom: '.65rem', lineHeight: 1.45 }}>
                    Prices include <strong style={{ color: 'var(--dark)' }}>{formatPrice(confirmedOrder.vatAmount)}</strong> VAT
                    {confirmedOrder.vatRatePercent != null ? ` (${confirmedOrder.vatRatePercent}%)` : ''}.
                  </p>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '.78rem', color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Total Paid</span>
                  <span style={{ fontWeight: 800, color: 'var(--teal-dark)', fontSize: '1.05rem' }}>{formatPrice(confirmedOrder.total)}</span>
                </div>
                <div style={{ borderTop: '1px solid var(--border-dark)', paddingTop: '.75rem' }}>
                  {confirmedOrder.items.map((item) => (
                    <div key={item.itemId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.85rem', padding: '.2rem 0' }}>
                      <span style={{ color: 'var(--dark)' }}>{item.emoji} {item.name} x{item.qty}</span>
                      <span style={{ color: 'var(--gray)' }}>{formatPrice(item.price * item.qty)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{
                background: '#fff8e6',
                border: '1px solid var(--gold)',
                borderRadius: 'var(--radius-sm)',
                padding: '1rem',
                fontSize: '.85rem',
                color: '#7a5500',
                lineHeight: 1.6,
              }}>
                📍 Collect at the <strong>Thai Express kiosk, Beau Vallon</strong>. Just show this screen or your confirmation number.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          {step === 'cart' && cart.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(true)}
                style={{
                  width: '100%',
                  marginBottom: '1rem',
                  padding: '0.7rem 1rem',
                  background: 'none',
                  border: '1px solid rgba(200, 72, 72, 0.45)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--red)',
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  transition: 'background .15s, border-color .15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(200, 72, 72, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                }}
              >
                Delete order
              </button>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.88rem', color: 'var(--gray)', padding: '.2rem 0' }}>
                  <span>
                    {cartCount} item{cartCount !== 1 ? 's' : ''}
                    {!tax.pricesIncludeVat && (
                      <span style={{ display: 'block', fontSize: '.72rem', opacity: 0.9 }}>excl. VAT</span>
                    )}
                  </span>
                  <span>{formatPrice(tax.subtotal)}</span>
                </div>
                {!tax.pricesIncludeVat && tax.vatRatePercent > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.88rem', color: 'var(--gray)', padding: '.25rem 0 0' }}>
                    <span>VAT ({tax.vatRatePercent}%)</span>
                    <span>{formatPrice(tax.vatAmount)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.05rem', color: 'var(--dark)', padding: '.45rem 0 0' }}>
                  <span>Total</span>
                  <span style={{ color: 'var(--teal-dark)' }}>{formatPrice(tax.totalDue)}</span>
                </div>
                <div style={{ marginTop: '.4rem' }}>
                  {tax.pricesIncludeVat ? (
                    <p style={{ fontSize: '.75rem', color: 'var(--gray)', lineHeight: 1.55, margin: 0 }}>
                      {tax.vatRatePercent > 0 ? (
                        <>
                          Prices include <strong style={{ color: 'var(--dark)', fontWeight: 700 }}>{formatPrice(tax.vatAmount)}</strong> VAT
                          {` (${tax.vatRatePercent}%)`}.
                        </>
                      ) : (
                        'No VAT on these items.'
                      )}
                    </p>
                  ) : (
                    <p style={{ fontSize: '.75rem', color: 'var(--gray)', lineHeight: 1.55, margin: 0 }}>
                      {tax.vatRatePercent > 0 ? (
                        <>
                          Price is subject to additional VAT of <strong style={{ color: 'var(--dark)', fontWeight: 700 }}>{formatPrice(tax.vatAmount)}</strong>
                          {', '}
                          so total to pay is <strong style={{ color: 'var(--teal-dark)', fontWeight: 700 }}>{formatPrice(tax.totalDue)}</strong>.
                        </>
                      ) : (
                        'No VAT on these items.'
                      )}
                    </p>
                  )}
                </div>
                <p style={{ fontSize: '.72rem', color: 'var(--gray)', marginTop: '.35rem', opacity: 0.95 }}>
                  Kiosk pickup, no delivery fee.
                </p>
              </div>
              <button
                className="btn btn-teal"
                style={{ width: '100%', justifyContent: 'center', fontSize: '1rem', padding: '1rem' }}
                onClick={() => setStep('checkout')}
              >
                Proceed to Checkout
              </button>
            </>
          )}

          {step === 'checkout' && (
            <>
              {!tax.pricesIncludeVat && tax.vatRatePercent > 0 && (
                <div style={{ marginBottom: '.65rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', color: 'var(--gray)', padding: '.2rem 0' }}>
                    <span>Subtotal (excl. VAT)</span>
                    <span>{formatPrice(tax.subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', color: 'var(--gray)', padding: '.2rem 0' }}>
                    <span>VAT ({tax.vatRatePercent}%)</span>
                    <span>{formatPrice(tax.vatAmount)}</span>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.05rem', color: 'var(--dark)', marginBottom: '.5rem' }}>
                <span>Total to pay</span>
                <span style={{ color: 'var(--teal-dark)' }}>{formatPrice(tax.totalDue)}</span>
              </div>
              {tax.pricesIncludeVat && tax.vatRatePercent > 0 && (
                <p style={{ marginBottom: '1rem', fontSize: '.78rem', color: 'var(--gray)', lineHeight: 1.5 }}>
                  Prices include <strong style={{ color: 'var(--dark)' }}>{formatPrice(tax.vatAmount)}</strong> VAT ({tax.vatRatePercent}%).
                </p>
              )}
              {!tax.pricesIncludeVat && tax.vatRatePercent > 0 && (
                <p style={{ marginBottom: '1rem', fontSize: '.78rem', color: 'var(--gray)', lineHeight: 1.5 }}>
                  Price is subject to additional VAT of <strong style={{ color: 'var(--dark)' }}>{formatPrice(tax.vatAmount)}</strong>
                  {'. '}
                  Total to pay is <strong style={{ color: 'var(--teal-dark)' }}>{formatPrice(tax.totalDue)}</strong>.
                </p>
              )}
              {tax.vatRatePercent === 0 && (
                <p style={{ marginBottom: '1rem', fontSize: '.78rem', color: 'var(--gray)', lineHeight: 1.5 }}>
                  No VAT on these items.
                </p>
              )}
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', fontSize: '1rem', padding: '1rem', marginBottom: '.6rem' }}
                onClick={handleConfirm}
              >
                Pay and confirm order
              </button>
              <button
                onClick={() => { setErrors({}); setStep('cart'); }}
                style={{
                  width: '100%', background: 'none', border: 'none',
                  color: 'var(--gray)', fontSize: '.85rem', padding: '.5rem',
                }}
              >
                Back to order
              </button>
            </>
          )}

          {step === 'success' && (
            <button
              className="btn btn-teal"
              style={{ width: '100%', justifyContent: 'center', padding: '1rem' }}
              onClick={resetAndClose}
            >
              Place Another Order
            </button>
          )}
        </div>
      </div>

      {deleteConfirmOpen && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1200,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.25rem',
            animation: 'cartConfirmFadeIn .2s ease',
          }}
          onClick={() => setDeleteConfirmOpen(false)}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cart-delete-title"
            aria-describedby="cart-delete-desc"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 360,
              background: '#fff',
              borderRadius: 'var(--radius)',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
              padding: '1.5rem 1.35rem 1.35rem',
              fontFamily: 'var(--font-body)',
            }}
          >
            <h3
              id="cart-delete-title"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.25rem',
                color: 'var(--dark)',
                margin: '0 0 .65rem',
                lineHeight: 1.3,
              }}
            >
              Remove all items?
            </h3>
            <p
              id="cart-delete-desc"
              style={{
                margin: '0 0 1.35rem',
                fontSize: '.92rem',
                lineHeight: 1.55,
                color: 'var(--gray)',
              }}
            >
              This will clear your basket. You can add dishes again from the menu anytime.
            </p>
            <div style={{ display: 'flex', gap: '.65rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(false)}
                className="btn btn-outline"
                style={{
                  flex: '1 1 120px',
                  justifyContent: 'center',
                  padding: '.75rem 1rem',
                  fontSize: '.92rem',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteOrder}
                style={{
                  flex: '1 1 120px',
                  justifyContent: 'center',
                  padding: '.75rem 1rem',
                  fontSize: '.92rem',
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: 'var(--radius-pill)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  background: 'var(--red)',
                  color: '#fff',
                  transition: 'background .15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--red-dark)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--red)'; }}
              >
                Delete order
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes cartConfirmFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
}
